import { theme as twTheme } from 'styles/tailwind';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Custom twMerge configuration that understands our design system tokens
 *
 * This extends the default tailwind-merge to recognize our custom spacing,
 * colors, typography, and other design tokens defined in tailwind.config.ts
 */

// Extract tokens from the Tailwind config
const extractTokens = (theme: typeof twTheme) => {
  return {
    spacing: Object.keys(theme.spacing || {}),
    borderRadius: Object.keys(theme.borderRadius || {}),
    borderWidth: Object.keys(theme.borderWidth || {}),
    colors: Object.keys(theme.colors || {}),
    zIndex: Object.keys(theme.zIndex || {}),
    boxShadow: Object.keys(theme.boxShadow || {}),
    fontFamily: Object.keys(theme.fontFamily || {}),
    fontSize: Object.keys(theme.fontSize || {}),
    fontWeight: Object.keys(theme.fontWeight || {}),
    lineHeight: Object.keys(theme.lineHeight || {}),
    letterSpacing: Object.keys(theme.letterSpacing || {}),
  };
};

const tokens = extractTokens(twTheme);

export const twMerge = extendTailwindMerge<
  | 'poly-font-size'
  | 'border-w'
  | 'border-w-t'
  | 'border-w-r'
  | 'border-w-b'
  | 'border-w-l'
  | 'border-w-x'
  | 'border-w-y'
  | 'z'
  | 'shadow'
  | 'font-family'
  | 'font-weight'
  | 'leading'
  | 'tracking'
  | 'rounded'
>({
  extend: {
    theme: {
      spacing: tokens.spacing,
      color: tokens.colors,
    },
    classGroups: {
      'border-w': [{ border: tokens.borderWidth }],
      'border-w-t': [{ 'border-t': tokens.borderWidth }],
      'border-w-r': [{ 'border-r': tokens.borderWidth }],
      'border-w-b': [{ 'border-b': tokens.borderWidth }],
      'border-w-l': [{ 'border-l': tokens.borderWidth }],
      'border-w-x': [{ 'border-x': tokens.borderWidth }],
      'border-w-y': [{ 'border-y': tokens.borderWidth }],
      z: [
        {
          z: tokens.zIndex,
        },
      ],
      shadow: [
        {
          shadow: tokens.boxShadow,
        },
      ],
      'font-family': [
        {
          font: tokens.fontFamily,
        },
      ],
      // Separate group from built-in 'font-size': standard text-xl etc. set both
      // font-size AND line-height, so tailwind-merge makes font-size conflict with
      // leading. Our tokens only set font-size — putting them in a separate group
      // prevents them from stripping leading-* classes.
      'poly-font-size': [
        {
          text: tokens.fontSize,
        },
      ],
      'font-weight': [
        {
          font: tokens.fontWeight,
        },
      ],
      leading: [
        {
          leading: tokens.lineHeight,
        },
      ],
      tracking: [
        {
          tracking: tokens.letterSpacing,
        },
      ],
      rounded: [{ rounded: tokens.borderRadius }],
    },
  },
});
